import type { ColumnDef } from "@tanstack/react-table";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTable,
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import { useAddressListQuery } from "@/services/backend/company/address-service";
import type { Address } from "@/services/backend/company/address";
import { Badge } from "@/shared/components/ui/badge";
import { EditAddressDialog } from "./edit-address-dialog";
import { DeleteAddressButton } from "./delete-address-button";

const tableColumns: ColumnDef<Address>[] = [
  {
    id: "address",
    accessorKey: "street",
    header: "Adresse",
    cell: ({ row }) => {
      const a = row.original;
      return (
        <div>
          {a.street} {a.houseNumber}, {a.postalCode} {a.city}, {a.country}
        </div>
      );
    },
  },
  {
    id: "flags",
    header: "Kennzeichnung",
    cell: ({ row }) => (
      <div className="flex flex-row gap-2">
        {row.original.isPrimaryAddress && <Badge>Hauptadresse</Badge>}
        {row.original.isInvoicingAddress && (
          <Badge variant="secondary">Rechnungsadresse</Badge>
        )}
      </div>
    ),
  },
  {
    id: "Optionen",
    accessorKey: "id",
    header: "Optionen",
    cell: ({ row }) => (
      <div className="flex flex-row items-center gap-2">
        <EditAddressDialog address={row.original} />
        <DeleteAddressButton addressId={row.original.id} />
      </div>
    ),
  },
];

interface AddressListTableProps {
  companyId: string;
}

export function AddressListTable({ companyId }: AddressListTableProps) {
  const {
    data: list,
    isLoading,
    error,
  } = useAddressListQuery(
    {
      companyId: {
        active: true,
        values: [companyId],
      },
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const table = useDataTableController(tableColumns, list?.items || []);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <DataTableContainer>
      <DataTable table={table} columns={tableColumns} isLoading={isLoading} />
    </DataTableContainer>
  );
}
